import axios, { type CancelTokenSource } from 'axios';

import { TNotificationConfig } from '@client/types';
import axiosInstance from '@client/utils/axios.util';
import { getTranslatedMessage } from '@client/utils/i18n.util';
import { logger } from '@client/utils/logger.util';

import type { UploadProps } from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';
import type { NotificationInstance } from 'antd/es/notification/interface';
import type { RefObject } from 'react';

export type UploadRequestOption = Parameters<
  NonNullable<UploadProps['customRequest']>
>[0];

export type TUploadOptions = {
  file: UploadRequestOption['file'];
  onProgress?: (progress: { percent: number }) => void;
  onError?: (error: any) => void;
  onSuccess?: (response: any, file: UploadRequestOption['file']) => void;
};

export type TUploadProps = {
  setUploading: (uploading: boolean) => void;
  cancelTokenRef: RefObject<CancelTokenSource>;
  executeRecaptcha: (action: string) => Promise<string>;
  accessToken: string;
  action: string;
  setPercent: (percent: number) => void;
  notificationApi: NotificationInstance;
  info: {
    defaultRequest: (option: UploadRequestOption) => void;
  };
} & TUploadOptions &
  UploadRequestOption;

export const uploadRequest = async (options: TUploadProps) => {
  const {
    file,
    onProgress,
    onError,
    onSuccess,
    setUploading,
    cancelTokenRef,
    executeRecaptcha,
    accessToken,
    action,
    setPercent,
    notificationApi,
  } = options;

  const t = getTranslatedMessage;

  setUploading(true);

  // Create a cancel token for this request
  cancelTokenRef.current = axios.CancelToken.source();

  try {
    // Execute reCAPTCHA before upload
    const recaptchaToken = await executeRecaptcha('file_upload');

    const formData = new FormData();
    formData.append('file', file);

    // Upload with Axios
    const response = await axiosInstance.post(action, formData, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'multipart/form-data',
        'recaptcha-token': recaptchaToken,
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          setPercent(percentCompleted);
          onProgress?.({ percent: percentCompleted });
        }
      },
      cancelToken: cancelTokenRef.current.token,
    });

    setUploading(false);
    setPercent(0);
    cancelTokenRef.current = null;

    // Antd Upload expects the response to be in a specific format
    onSuccess?.(response.data, file);
  } catch (error: any) {
    setUploading(false);
    setPercent(0);

    // Don't show error if request was cancelled
    if (axios.isCancel(error)) {
      logger.log('Upload cancelled:', error.message);
      return;
    }

    logger.error('Upload error:', error);

    // Handle different error types
    let errorMessage = t('error.uploadFailed');

    if (axios.isAxiosError(error)) {
      if (error.response) {
        // Server responded with error status
        const serverError =
          error.response.data?.error || error.response.data?.message;

        if (error.response.status === 413) {
          errorMessage = t('error.fileTooLarge');
        } else if (error.response.status === 401) {
          errorMessage = t('error.unauthorized');
        } else if (error.response.status === 400) {
          errorMessage = serverError || t('error.badRequest');
        } else {
          errorMessage =
            serverError || `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = t('error.networkError');
      } else {
        // Something else happened
        errorMessage = error.message;
      }
    }

    onError?.(error);
    notificationApi.error({
      title: t('error.uploadError'),
      description: errorMessage,
      ...TNotificationConfig,
    });
  } finally {
    cancelTokenRef.current = null;
  }
};

type TCancelOptions = {
  setUploading: (uploading: boolean) => void;
  cancelTokenRef: RefObject<CancelTokenSource>;
  setPercent: (percent: number) => void;
  messageApi: MessageInstance;
};

export const cancelUpload = async (options: TCancelOptions) => {
  const { cancelTokenRef, setUploading, setPercent, messageApi } = options;

  if (cancelTokenRef.current) {
    cancelTokenRef.current.cancel('Upload cancelled by user');
    setUploading(false);
    setPercent(0);
    messageApi.info('Upload cancelled');
  }
};
